import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import FormContainer from '../../../components/Form/FormContainer'
import LegacyMultiUnitDimensionFields from '../Accounting/LegacyMultiUnitDimensionFields'
import { AddDimensionValues } from '../Accounting/CreateDimensionDialog'
import WorkInProgressFields from '../Finance/WorkInProgressFields'
import RateFields from './Rate/RateFields'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Field, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import { PivotProps } from './FileForm'
import { useFileModules } from '../../../hooks/module/useFileModule'
import CurrencyCombobox, { CurrencyComboboxProps } from '../../Combobox/i18n/CurrencyCombobox'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../Combobox/i18n/VatTypeCombobox'
import BudgetMonitoringFields from '../Finance/BudgetMonitoringFields'
import { useTranslation } from 'react-i18next'
import UnitCombobox from '../../Combobox/UnitCombobox'
import { useSettings } from '../../../hooks/useSettings'
import { EntityType } from '../Accounting/EntityField'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import FormikInputField from '../../Formik/FormikInputField'
import { formatMultipleEmailAddresses } from '../../../helpers/regexHelper'

const useStyles = makeStyles({
    formRow: {
        display: 'grid',
        ...shorthands.margin(tokens.spacingHorizontalM),
        marginLeft: tokens.spacingHorizontalXXL,
        ...shorthands.gap(tokens.spacingVerticalS),
    },
})

export default function FinancePivotItem(props: PivotProps): ReactElement {
    const { settings } = useSettings()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<FileMutationDto>()
    const modules = useModules()
    const fileModules = useFileModules(values)

    const createDialogInitialValues = useMemo<AddDimensionValues>(
        () => ({
            code: values.reference,
            name: values.name,
        }),
        [values.reference, values.name],
    )

    const defaultOpenAccordionItems = useMemo(() => {
        return values.unit ? ['rates'] : ['rates', 'accounting-choice']
    }, [values.unit])

    return (
        <FormContainer>
            <Accordion multiple collapsible defaultOpenItems={defaultOpenAccordionItems}>
                {modules.has(ModuleType.TimeTracking) && (
                    <AccordionItem value="rates">
                        <AccordionHeader size="large">{translate('file.edit.form.finance.rates.label')}</AccordionHeader>
                        <AccordionPanel>
                            <RateFields styles={styles} orientation={props.orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                )}
                <AccordionItem value="invoice">
                    <AccordionHeader size="large">{translate('file.edit.form.finance.invoice')}</AccordionHeader>
                    <AccordionPanel>
                        <div className={styles.formRow}>
                            <FormikComboboxField<FileMutationDto, CurrencyComboboxProps>
                                required
                                labelKey={'file.edit.form.finance.currency'}
                                orientation={props.orientation}
                                fieldName={'currency'}
                                component={CurrencyCombobox}
                            />
                            {!!settings?.invoicePaymentCurrency && (
                                <FormikComboboxField<FileMutationDto, CurrencyComboboxProps>
                                    required
                                    labelKey={'file.edit.form.finance.paymentcurrency'}
                                    orientation={props.orientation}
                                    fieldName={'paymentCurrency'}
                                    component={CurrencyCombobox}
                                />
                            )}
                            <FormikComboboxField<FileMutationDto, VatTypeComboboxProps>
                                required
                                labelKey={'file.edit.form.finance.vat'}
                                orientation={props.orientation}
                                fieldName={'vatType'}
                                component={VatTypeCombobox}
                            />

                            <FormikInputField<FileMutationDto>
                                labelKey={'file.edit.form.finance.invoicename'}
                                orientation={props.orientation}
                                fieldName={'invoiceName'}
                            />
                            <FormikInputField<FileMutationDto>
                                labelKey={'file.edit.form.finance.invoiceemail'}
                                orientation={props.orientation}
                                fieldName={'invoiceEmail'}
                                componentProps={{
                                    onBlur: e => {
                                        const newValue = formatMultipleEmailAddresses(e.target.value)
                                        setFieldValue('invoiceEmail', newValue?.join(';'))
                                    },
                                }}
                            />
                        </div>
                    </AccordionPanel>
                </AccordionItem>
                {modules.has(ModuleType.Accounting) && (
                    <AccordionItem value="accounting-choice">
                        <AccordionHeader size="large">{translate('file.edit.form.finance.accounting')}</AccordionHeader>
                        <AccordionPanel>
                            <div className={styles.formRow}>
                                <Field label={translate('file.edit.form.finance.unit')} orientation={props.orientation}>
                                    <UnitCombobox
                                        selectedOptions={[values.unit]}
                                        onOptionSelect={(_, data) => {
                                            setFieldValue('unit', data.selectedOptions?.[0])
                                        }}
                                    />
                                </Field>
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                )}
                {modules.has(ModuleType.Accounting) && values.unit && (
                    <LegacyMultiUnitDimensionFields
                        styles={styles}
                        fieldName="accounting"
                        initialValues={createDialogInitialValues}
                        selectedUnit={values.unit}
                        selectedEntityTypes={[EntityType.CostCenter, EntityType.CostUnit, EntityType.GLA, EntityType.Project]}
                    />
                )}
                {fileModules.has(ModuleType.WorkInProgress) && (
                    <AccordionItem value="wip">
                        <AccordionHeader size="large">{translate('file.edit.form.finance.workinprogress.title')}</AccordionHeader>
                        <AccordionPanel>
                            <WorkInProgressFields styles={styles} orientation={props.orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                )}
                {fileModules.has(ModuleType.BudgetMonitoring) && (
                    <AccordionItem value="budgetmonitoring">
                        <AccordionHeader size="large">{translate('file.edit.form.finance.budgetmonitoring.title')}</AccordionHeader>
                        <AccordionPanel>
                            <BudgetMonitoringFields styles={styles} orientation={props.orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                )}
            </Accordion>
        </FormContainer>
    )
}
